import {
  Box,
  IconButton,
  Paper,
  Stack,
  Typography,
  Drawer,
} from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import DocsLayout from './DocsLayout'
import AmbuNetLogo from '../../assets/images/ambunetLogo.png'
import { UserContext } from '../../App'
import DocsSearch from './DocsSearch'
import { SiteModeContext } from '../../components/mainFrame/MainFrame'
import DocsSidebar from './DocsSidebar'
import MenuIcon from '@mui/icons-material/Menu'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import AutoStoriesRoundedIcon from '@mui/icons-material/AutoStoriesRounded'

// On the docs.<domain> subdomain the knowledgebase is the whole page, so there
// is no app-frame padding for the negative margin below to cancel out — it
// would push the layout 16px off each edge and force a sideways scroll.
const isDocsSubdomain = window.location.hostname.split('.')[0] === 'docs'

const PublicDocs = () => {
  const userContext = useContext(UserContext)
  const user = userContext?.user
  const isLoggedIn = !!user?._id

  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)

  const { sidebarState, setSidebarState } = useContext(SiteModeContext)

  useEffect(() => {
    if (!isLoggedIn) return
    let originalState = { ...sidebarState }
    setSidebarState({
      collapsed: true,
      mobileOpen: false,
    })

    return () => {
      setSidebarState(originalState)
    }
  }, [])

  return (
    <Box
      sx={{
        p: { xs: 1.25, md: 2.5 },
        m: isDocsSubdomain ? 0 : -2,
        minHeight: '100%',
        boxSizing: 'border-box',
        // The app has no global border-box reset (no <CssBaseline/>), so any
        // `width: 100%` element with padding or a border ends up wider than
        // its parent. That is invisible on desktop, where the docs column has
        // spare room, but pushes text off screen on a phone.
        '& *, & *::before, & *::after': { boxSizing: 'border-box' },
        overflowX: 'hidden',
        background:
          'linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 48%, #EAF1F6 100%)',
      }}
    >
      <Stack spacing={2.5}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: { xs: 2.5, md: 3 },
            p: { xs: 1.5, md: 2 },
            background:
              'linear-gradient(135deg, #1A2230 0%, #242F40 58%, #172033 100%)',
            boxShadow: '0 18px 50px rgba(15,23,42,0.16)',
            color: 'white',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box
                component="img"
                src={AmbuNetLogo}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  p: 0.6,
                  bgcolor: 'rgba(255,255,255,0.10)',
                  border: '1px solid rgba(255,255,255,0.16)',
                }}
              />

              <Box>
                <Typography
                  variant="h4"
                  fontWeight={950}
                  color="white"
                  sx={{
                    letterSpacing: 0,
                    fontSize: { xs: '1.05rem', md: '1.35rem' },
                    lineHeight: 1.15,
                  }}
                >
                  AmbuNet Knowledgebase
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255,255,255,0.68)',
                    fontSize: { xs: 12, md: 13 },
                  }}
                >
                  Fast answers for operational teams
                </Typography>
              </Box>
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              gap={2}
              sx={{
                width: { xs: '100%', md: 'auto' },
                flex: { xs: 1, md: '0 1 640px' },
              }}
            >
              <IconButton
                onClick={() => setMobileSidebarOpen(true)}
                aria-label="Open documentation navigation"
                sx={{
                  display: { xs: 'inline-flex', lg: 'none' },
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.10)',
                  borderRadius: 2,
                  width: 46,
                  height: 46,
                }}
              >
                <MenuIcon />
              </IconButton>

              <Box
                sx={{
                  flex: 1,
                  minWidth: { xs: 0, md: 420 },
                  width: { xs: '100%', md: 640 },
                }}
              >
                <DocsSearch />
              </Box>
            </Stack>
          </Stack>
        </Paper>

        <DocsLayout />
      </Stack>

      <Drawer
        open={mobileSidebarOpen}
        onClose={() => setMobileSidebarOpen(false)}
        PaperProps={{
          sx: {
            width: 'min(92vw, 360px)',
            bgcolor: '#F8FAFC',
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid rgba(15,23,42,0.08)',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <AutoStoriesRoundedIcon sx={{ color: '#239AA4' }} />
            <Typography fontWeight={700}>Browse docs</Typography>
          </Stack>

          <IconButton
            onClick={() => setMobileSidebarOpen(false)}
            aria-label="Close documentation navigation"
            sx={{ borderRadius: 2 }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Stack>

        <Box sx={{ p: 1.5 }}>
          <DocsSidebar mobile onNavigate={() => setMobileSidebarOpen(false)} />
        </Box>
      </Drawer>
    </Box>
  )
}

export default PublicDocs
